import { BellOff, BellRing, Bot, Check, Clock, UserPlus } from 'lucide-react';
import type { Conversation, Inbox, NotificationMute } from '../../api/types';
import { inboxGrant } from '../../lib/capabilities';
import { channelReach } from '../../lib/notificationReach';
import { useTimeFormat } from '../../lib/tenantTime';
import { useComms } from './context';
import { MUTE_OPTIONS, muteUntil, useNotifications } from './notifications';
import { SNOOZE_OPTIONS, type Run } from './threadMenus';

/**
 * The conversation controls both layouts draw — the three-pane header and the
 * mail layout's reading view — written once.
 *
 * Only the MENU BODIES and the strip under the header live here; each layout
 * keeps its own buttons and its own open-menu state, because the two disagree
 * about shape (bordered in the header, round in the mail chrome) and about
 * where a menu may hang. What they must never disagree about is what a row in
 * the menu does: who "Assign to me" reaches, how long "Until tomorrow" is,
 * whether a mute is the conversation's or the channel's. Two copies of that is
 * how one layout quietly starts muting something the other one does not.
 *
 * `onRun` is the host's own action runner — it closes the menu, reports a
 * refusal and re-reads the row — so the items stay free of either layout's
 * error plumbing. What the menus OFFER (the durations, the call routes, the
 * mute that is not a watch) is in threadMenus.ts, beside these.
 */

/** "Snooze until" and its durations. The host decides what snoozing does next. */
export function SnoozeMenuItems({ onSnooze }: { onSnooze: (hours: number) => void }) {
  return (
    <>
      <div className="thr-menu-note">Snooze until</div>
      {SNOOZE_OPTIONS.map((option) => (
        <button key={option.hours} type="button" onClick={() => onSnooze(option.hours)}>
          <Clock size={13} />
          {option.label}
        </button>
      ))}
    </>
  );
}

/**
 * Mute THIS conversation's notifications, or lift a mute already on it.
 *
 * A 'watch' row lives in the same table, and showing one here as "muted until
 * you unmute" would state the exact opposite of what it does — the host passes
 * mutes only (see NotifyStrips, which owns the watch state).
 */
export function MuteMenuItems({
  conversationId,
  mute: current,
  onDone,
}: {
  conversationId: number;
  mute: NotificationMute | null;
  /** Run the write; only the agent's own prefs change, so nothing is re-read. */
  onDone: Run;
}) {
  const { fmtDateTime } = useTimeFormat();
  const { mute, unmute } = useNotifications();
  if (current) {
    return (
      <>
        <div className="thr-menu-note">
          Notifications muted{' '}
          {current.mutedUntil ? `until ${fmtDateTime(current.mutedUntil)}` : 'until you unmute'}
        </div>
        <button type="button" onClick={() => onDone(() => unmute(current.id))}>
          <BellRing size={13} />
          Unmute notifications
        </button>
      </>
    );
  }
  return (
    <>
      <div className="thr-menu-note">Mute notifications</div>
      {MUTE_OPTIONS.map((option) => (
        <button
          key={option.label}
          type="button"
          onClick={() =>
            onDone(() =>
              mute({
                scope: 'conversation',
                conversationDisplayId: conversationId,
                until: muteUntil(option.hours),
              }),
            )
          }
        >
          <BellOff size={13} />
          {option.label}
        </button>
      ))}
    </>
  );
}

/** Who the conversation goes to: the assistant, this agent, a colleague, or nobody. */
export function AssignMenuItems({
  conversation,
  inbox,
  onRun,
}: {
  conversation: Conversation;
  inbox: Inbox | undefined;
  onRun: Run;
}) {
  const { source, agents, me } = useComms();
  const botOwns = conversation.status === 'pending' && conversation.assigneeIsBot;
  /**
   * Offer the hand-back only where it can work: a sending assistant answers
   * this channel and does not already have the thread. `botEnabled` is
   * undefined on an older server, which reads as no — the server refuses with
   * a reason either way, this only keeps a dead action out of the menu.
   */
  const canHandBackToBot = inbox?.botEnabled === true && !botOwns;
  return (
    <>
      {canHandBackToBot && (
        <button type="button" onClick={() => onRun(() => source.api.handBackToBot(conversation.id))}>
          <Bot size={13} />
          Hand back to the assistant
        </button>
      )}
      {me && (
        <button
          type="button"
          onClick={() => onRun(() => source.api.assignConversation(conversation.id, me.dashBoardId))}
        >
          <UserPlus size={13} />
          Assign to me
        </button>
      )}
      {/* Everybody the CRM can hand work to, whatever their rank — any role may
          assign to any other. Someone whose role lacks the CRM permission is
          still on the list (it is what assignees are named from) but could never
          open what they were given, so they are not offered. */}
      {agents
        .filter((agent) => agent.dashBoardId !== me?.dashBoardId && agent.hasCrmAccess !== false)
        .map((agent) => (
          <button
            key={agent.dashBoardId}
            type="button"
            onClick={() => onRun(() => source.api.assignConversation(conversation.id, agent.dashBoardId))}
          >
            {conversation.assigneeDashBoardId === agent.dashBoardId ? (
              <Check size={13} />
            ) : (
              <span style={{ width: 13 }} />
            )}
            {agent.name}
          </button>
        ))}
      <button type="button" onClick={() => onRun(() => source.api.assignConversation(conversation.id, null))}>
        <span style={{ width: 13 }} />
        Unassign
      </button>
    </>
  );
}

/**
 * The state worth surfacing under a thread's header: nothing on this thread will
 * reach this agent — or they asked to follow it anyway. Without the first an
 * agent watches a thread assuming they will hear about the reply. Muted with a
 * bell-off rather than styled as a warning: nothing is wrong, they just are not
 * on the hook.
 *
 * Resolved through channelReach, the settings screen's mirror of the server, so
 * the strip and that screen cannot disagree about one channel. Three reasons are
 * left, now that no channel can be switched off: the channel is on Only mine and
 * somebody else holds this thread; New messages is switched off; or the
 * assistant is handling it. A thread they muted says so on its own header
 * button and is not repeated here.
 */
export function NotifyStrips({
  conversation,
  inbox,
  onRun,
}: {
  conversation: Conversation;
  inbox: Inbox | undefined;
  onRun: Run;
}) {
  const { capabilities, agents, me } = useComms();
  const { muteFor, mute, unmute, preferences, inboxPreferences } = useNotifications();
  const override = muteFor('conversation', conversation.id);

  if (override?.mode === 'watch') {
    return (
      <div className="thr-unwatched is-watching">
        <BellRing size={14} />
        <span className="thr-unwatched-text">You&rsquo;re following this conversation.</span>
        <button type="button" className="cbtn cbtn-sm" onClick={() => onRun(() => unmute(override.id))}>
          <BellOff size={13} />
          Stop
        </button>
      </div>
    );
  }
  if (override || !inbox) return null;

  const reach = channelReach({
    inboxId: inbox.id,
    grant: inboxGrant(capabilities, inbox.id),
    preferences,
    override: inboxPreferences[String(inbox.id)] ?? {},
  });
  /**
   * Only mine lets through what is theirs or nobody's yet — the server's rule.
   * Compared as strings, like assignTitle: the two ids reach the console by
   * different routes. Unknown until `me` loads, which reads as no strip rather
   * than a false one.
   */
  const holder = conversation.assigneeDashBoardId;
  const someoneElses = holder != null && me != null && String(holder) !== String(me.dashBoardId);
  const outsideMine = reach.width === 'mine' && someoneElses;
  const hearsMessages = reach.blocked === null && reach.types.includes('new_message') && !outsideMine;
  /**
   * The assistant's conversations reach nobody unless they follow its threads,
   * which takes the chatbot permission as well as the toggle — the server
   * re-checks the permission on every send, and it is checked ahead of a watch,
   * so Notify me is offered here only to someone who holds it.
   */
  const botAccess = capabilities?.access?.features?.bot === 'manage';
  const botQuiet =
    conversation.assigneeIsBot && !(botAccess && preferences?.follow_bot_chats === true);
  if (hearsMessages && !botQuiet) return null;

  const holderName = someoneElses
    ? (agents.find((agent) => String(agent.dashBoardId) === String(holder))?.name ?? null)
    : null;
  const why =
    reach.blocked === 'no-access' ? (
      <>You won&rsquo;t be notified about this conversation.</>
    ) : outsideMine ? (
      <>
        {holderName ? (
          <>
            Assigned to <b>{holderName}</b>.
          </>
        ) : (
          <>Assigned to a colleague.</>
        )}{' '}
        This channel is set to Only mine, so you won&rsquo;t be notified about it.
      </>
    ) : !hearsMessages ? (
      <>New-message notifications are off for this channel, so you won&rsquo;t hear when they write.</>
    ) : (
      <>You won&rsquo;t be notified while the assistant is handling this conversation.</>
    );
  return (
    <div className="thr-unwatched">
      <BellOff size={14} />
      <span className="thr-unwatched-text">{why}</span>
      {(!conversation.assigneeIsBot || botAccess) && (
        <button
          type="button"
          className="cbtn cbtn-sm"
          onClick={() =>
            onRun(() =>
              mute({
                scope: 'conversation',
                mode: 'watch',
                conversationDisplayId: conversation.id,
                until: null,
              }),
            )
          }
        >
          <BellRing size={13} />
          Notify me
        </button>
      )}
    </div>
  );
}
